"use client";

import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { apiFetch } from "@/lib/api/client";
import type { DashboardSummary } from "../types";

type Props = { selectedDate: string };

export default function TaskCompletionChart({ selectedDate }: Props) {
  const [data, setData] = useState<DashboardSummary['charts']['taskCompletion'] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const queryParams = selectedDate ? `?date=${selectedDate}` : "";
        const res = await apiFetch<DashboardSummary['charts']['taskCompletion']>(`/api/v1/dashboard/charts/tasks${queryParams}`, { method: "GET", auth: true });
        if (active) setData(res);
      } catch (error) {
        console.error("Failed to load task completion chart data:", error);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [selectedDate]);

  const weekly = data?.weekly ?? [];
  const monthly = data?.monthlyFilings ?? [];

  const latest = weekly[weekly.length - 1];
  const previous = weekly[weekly.length - 2];
  const latestRate = latest && latest.total > 0 ? Math.round((latest.completed / latest.total) * 100) : 0;
  const prevRate = previous && previous.total > 0 ? Math.round((previous.completed / previous.total) * 100) : 0;
  const diff = latestRate - prevRate;

  if (loading || !data) {
    return <div className="bg-white rounded-xl border border-border shadow-card p-5 h-[460px] animate-pulse" />;
  }

  return (
    <div className="bg-white rounded-xl border border-border shadow-card p-5 flex flex-col gap-5">
      <div>
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="text-[15px] font-600 text-foreground">Task Completion Trend</h3>
            <p className="text-[12px] text-muted-foreground mt-0.5">8-week rolling view</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-700 tabular-nums text-foreground">{latestRate}%</p>
            <p className={`text-[12px] font-500 ${diff >= 0 ? "text-green-600" : "text-red-500"}`}>
              {diff >= 0 ? "up" : "down"} {Math.abs(diff)}% vs prev week
            </p>
          </div>
        </div>
        <div className="h-44 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={weekly} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="completedGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#16a34a" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="overdueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#dc2626" stopOpacity={0.12} />
                  <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Area type="monotone" dataKey="completed" stroke="#16a34a" strokeWidth={2} fill="url(#completedGrad)" dot={{ r: 3, fill: "#16a34a", strokeWidth: 0 }} />
              <Area type="monotone" dataKey="overdue" stroke="#dc2626" strokeWidth={2} fill="url(#overdueGrad)" dot={{ r: 3, fill: "#dc2626", strokeWidth: 0 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-[14px] font-600 text-foreground">Monthly Filings</h3>
            <p className="text-[11px] text-muted-foreground">New cases filed per month</p>
          </div>
          <span className="text-[11px] font-500 bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Last 6 months</span>
        </div>
        <div className="h-36">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly} margin={{ top: 4, right: 4, left: -20, bottom: 0 }} barSize={8} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Bar dataKey="patent" fill="#2563eb" radius={[2, 2, 0, 0]} />
              <Bar dataKey="trademark" fill="#7c3aed" radius={[2, 2, 0, 0]} />
              <Bar dataKey="design" fill="#d97706" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
